﻿import {isNullOrEmpty} from "@2d-front-monorepo/core";
import React, {JSX, ReactNode} from "react";
import Icon from "../Icon";
import {PrefixSuffixType} from "./HtmlControlProps";

export interface LabeledControlAriaProps {
	describedBy?: string;
	invalid?: boolean;
}

export interface LabeledControlProps {
	label: ReactNode,
	type: string,
	id: string,
	column?: string,
	hint?: string,
	error?: string,
	inlineError?: boolean,
	required?: boolean,
	disabled?: boolean,
	clickable?: boolean,
	hidden?: boolean,
	children?: ReactNode,
	footer?: ReactNode,
	ariaRef?: React.RefObject<LabeledControlAriaProps | undefined>;
	controlPrefix?: PrefixSuffixType;
	controlSuffix?: PrefixSuffixType;
	ref?: React.RefObject<HTMLSpanElement | null>;
}

//El CSS pega prefijo/sufijo al control tratando a cada pieza como un elemento; un string suelto
//quedaría como nodo de texto sin caja, así que se le da la suya.
function asPiece(value: PrefixSuffixType): ReactNode {
	return typeof value === "string" ? <span>{value}</span> : value;
}

export default function LabeledControl(props: LabeledControlProps): JSX.Element {
	const {
		label,
		type,
		id,
		column,
		hint,
		error,
		inlineError = true,
		required,
		disabled,
		clickable,
		hidden,
		children,
		footer,
		ariaRef,
		controlPrefix,
		controlSuffix,
		ref
	} = props;

	const hintId = id + "-hint";
	const errorId = id + "-error";
	const hasError = !isNullOrEmpty(error);
	const hasHint = hint != null;

	if (ariaRef) {
		ariaRef.current = {
			describedBy: ((hasHint ? hintId : "") + (hasError ? " " + errorId : "")).trimStart(),
			invalid: hasError
		};
	}

	const klass = "labelled-field" + (column ? ` ${column}` : "");
	const klassType = "form__field form__field--" + type + (clickable ? " form__field--clickable" : "");

	if (hidden) {
		return <></>;
	}

	return <div className={klass}>
		<div className={klassType}>
			<label className={"field__label" + (hasError ? " field__label--error" : "")} htmlFor={id}>
				<span className="label__text">
					{label} {required && !disabled && <span className="field__star" aria-hidden="true">*</span>}
				</span>
			</label>
			<span className={"field__input" + (hasError ? " field__input--error" : "")} ref={ref}>
				{controlPrefix && <span className="field__prefix">{asPiece(controlPrefix)}</span>}
				{children}
				{controlSuffix && <span className="field__suffix">{asPiece(controlSuffix)}</span>}
			</span>
			{(inlineError || hasHint) && <span
                className={"field__messages" +
					(hasError ? " field__messages--error" : "") +
					(hasError || hasHint ? " field__messages--open" : "")}>
				<span className="field__messages-content">
					{inlineError && <span
                        id={errorId}
                        className="label__error"
                        aria-live="polite"
                        aria-hidden={!error}>
						<Icon icon="input-validation-error"/>
						<span>{error}</span>
					</span>}
					{hasHint && <span id={hintId} className="field__hint">{hint}</span>}
				</span>
			</span>}
			{footer}
		</div>
	</div>;
};
